import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  CloudUpload as UploadIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  GetApp as TakeIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from './api';
import ConfirmModal from './ConfirmModal';
import WorkOrderDetailsDialog from './WorkOrderDetailsDialog';

const WorkOrders = () => {
  const location = useLocation();
  const userId = useSelector(state => state.auth_reducer?.user_id);
  const companyId = useSelector(state => state.project_reducer?.temp_com_id || state.project_reducer?.company_id);

  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteWorkOrderId, setDeleteWorkOrderId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCompletionDialog, setOpenCompletionDialog] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [relatedAlarm, setRelatedAlarm] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [employees, setEmployees] = useState([]);
  const [skills, setSkills] = useState([]);
  const [employeeSkills, setEmployeeSkills] = useState([]);
  const [locations, setLocations] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [operations, setOperations] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedWorkOrderForDetails, setSelectedWorkOrderForDetails] = useState(null);

  useEffect(() => {
    // If navigated from alarm, avoid noisy load errors; load after creation instead
    const fromAlarm = !!location.state?.createFromAlarm;
    loadWorkOrders(fromAlarm /* silent */);
    loadEmployees().then(loadEmployeeSkills);
    loadSkills();
    loadLocations();
    loadEquipment();
    loadOperations();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadWorkOrders(true /* silent */);
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadEmployees = async () => {
    try {
      const data = await api.employees.getAll();
      setEmployees(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
      return [];
    }
  };

  const loadSkills = async () => {
    try {
      const data = await api.skills.getAll();
      setSkills(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading skills:', error);
      setSkills([]);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await api.locations.getAll();
      setLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  };

  const loadEquipment = async () => {
    try {
      const data = await api.equipment.getAll();
      setEquipment(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading equipment:', error);
      setEquipment([]);
    }
  };

  const loadOperations = async () => {
    try {
      const data = await api.operations.getAll();
      setOperations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading operations:', error);
      setOperations([]);
    }
  };

  const loadEmployeeSkills = async (employeeList = employees) => {
    try {
      const allEmployeeSkills = [];
      // Load skills for each employee
      for (const employee of employeeList) {
        try {
          const skills = await api.employees.getSkills(employee.Id);
          if (Array.isArray(skills) && skills.length > 0) {
            allEmployeeSkills.push(...skills.map(skill => ({
              ...skill,
              EmployeeId: employee.Id
            })));
          }
        } catch (error) {
          // Employee has no skills or error fetching
          console.log(`No skills for employee ${employee.Id}`);
        }
      }
      setEmployeeSkills(allEmployeeSkills);
    } catch (error) {
      console.error('Error loading employee skills:', error);
      setEmployeeSkills([]);
    }
  };

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return '-';
    const employee = employees.find(emp => emp.Id.toString() === employeeId.toString());
    if (employee) {
      return `${employee.FirstName} ${employee.LastName}`;
    }
    return employeeId; // fallback to ID if employee not found
  };

  // Open dialog if navigated from alarm
  useEffect(() => {
    const state = location.state;
    if (state?.createFromAlarm && state.alarm) {
      setRelatedAlarm(state.alarm);
      setSelectedWorkOrder(null);
      setOpenDialog(true);
    }
  }, [location.state]);

  const loadWorkOrders = async (silent = true) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.workOrders.list();
      setWorkOrders(response || []);
      setLastRefresh(Date.now());
    } catch (error) {
      console.error('Error loading work orders:', error);
      if (!silent) toast.error('İş emirleri yüklenemedi');
      setWorkOrders([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleManualRefresh = () => {
    loadWorkOrders(false);
  };

  const handleCreateWorkOrder = () => {
    setSelectedWorkOrder(null);
    setOpenDialog(true);
  };


  const handleViewDetails = (workOrder) => {
    setSelectedWorkOrderForDetails(workOrder);
    setDetailsDialogOpen(true);
  };

  const handleDetailsDialogClose = () => {
    setDetailsDialogOpen(false);
    setSelectedWorkOrderForDetails(null);
    // Reload work orders to get updated data
    loadWorkOrders(true);
  };


  const handleCloseWorkOrder = async (workOrderId) => {
    try {
      await api.workOrders.closeWorkOrder(workOrderId);
      toast.success('İş emri kapatıldı');
      loadWorkOrders();
    } catch (error) {
      console.error('Error closing work order:', error);
      toast.error('İş emri kapatılamadı');
    }
  };

  const handleDeleteWorkOrder = (id) => {
    setDeleteWorkOrderId(id);
    setConfirmOpen(true);
  };


  const handleCompleteWorkOrder = async (completionData) => {
    try {
      if (!selectedWorkOrder) return;

      await api.workOrders.complete(selectedWorkOrder.workOrderID, completionData);
      toast.success(`İş emri tamamlandı: ${completionData.finalStatus}`);

      setOpenCompletionDialog(false);
      setSelectedWorkOrder(null);
      await loadWorkOrders();
    } catch (error) {
      console.error('Error completing work order:', error);
      toast.error('İş emri tamamlanamadı');
    }
  };

  const confirmDelete = async () => {
    try {
      toast.info('Silme özelliği henüz eklenmedi');
    } catch (error) {
      console.error('Error deleting work order:', error);
      toast.error('İş emri silinemedi');
    }
  };

  const handleSaveWorkOrder = async (workOrderData) => {
    console.log('handleSaveWorkOrder called with:', workOrderData);
    try {
      if (selectedWorkOrder) {
        // Update existing work order
        console.log('Updating work order with data:', workOrderData);
        const res = await api.workOrders.update(selectedWorkOrder.workOrderID, workOrderData);
        toast.success(`İş emri #${selectedWorkOrder.workOrderID} güncellendi`);
      } else {
        // Create work order; if arrived from an alarm, tie it
        if (relatedAlarm) {
          const res = await api.alarms.createWorkOrder({
            alarmId: relatedAlarm.id,
            title: workOrderData.title,
            description: workOrderData.description,
            assignedUserId: workOrderData.assignedUserId,
            dueDate: workOrderData.dueDate,
            priority: workOrderData.priority,
            createdByUserId: workOrderData.createdByUserId,
            companyId: workOrderData.companyId,
            location: workOrderData.location,
            equipment: workOrderData.equipment
          });
          const woId = res?.WorkOrderId || res?.workOrderId || 'N/A';
          toast.success(`Alarm #${relatedAlarm.id} için iş emri #${woId} oluşturuldu`);
        } else {
          console.log('Sending workOrderData:', JSON.stringify(workOrderData, null, 2));
          const res = await api.workOrders.createUserRequest(workOrderData);
          const woId = res?.WorkOrderId || res?.workOrderId || 'N/A';
          toast.success(`İş emri #${woId} oluşturuldu`);
        }
      }
      setOpenDialog(false);
      setRelatedAlarm(null);
      await loadWorkOrders(/* silent */ true);
    } catch (error) {
      console.error('Error saving work order:', error);
      console.error('Error response:', error.response?.data);
      toast.error('İş emri kaydedilemedi');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'created': return 'warning';
      case 'inprogress': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const filteredWorkOrders = workOrders.filter(wo => {
    const status = wo.workOrderStatus?.toLowerCase();
    if (filter !== 'all' && status !== filter) return false;
    if (searchTerm && !wo.title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !wo.workOrderID?.toString().includes(searchTerm)) return false;
    return true;
  });

  const stats = {
    total: workOrders.length,
    pending: workOrders.filter(wo => wo.workOrderStatus?.toLowerCase() === 'open').length,
    inProgress: workOrders.filter(wo => wo.workOrderStatus?.toLowerCase() === 'inprogress').length,
    completed: workOrders.filter(wo => wo.workOrderStatus?.toLowerCase() === 'completed').length
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon />
          İş Emirleri Yönetimi
        </Typography>
        {autoRefresh && (
          <Typography variant="caption" color="text.secondary">
            Son güncelleme: {new Date(lastRefresh).toLocaleTimeString()}
          </Typography>
        )}
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Toplam</Typography>
              <Typography variant="h3">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Bekleyen</Typography>
              <Typography variant="h3" color="warning.main">{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Devam Eden</Typography>
              <Typography variant="h3" color="info.main">{stats.inProgress}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Tamamlanan</Typography>
              <Typography variant="h3" color="success.main">{stats.completed}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="İş emirlerinde ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Duruma Göre Filtrele</InputLabel>
              <Select
                value={filter}
                label="Duruma Göre Filtrele"
                onChange={(e) => setFilter(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="open">Açık</MenuItem>
                <MenuItem value="inprogress">Devam Ediyor</MenuItem>
                <MenuItem value="completed">Tamamlandı</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
            <IconButton
              onClick={handleManualRefresh}
              disabled={loading}
              title="İş emirlerini yenile"
              sx={{ mr: 1 }}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              onClick={() => setAutoRefresh(!autoRefresh)}
              color={autoRefresh ? 'primary' : 'default'}
              title={autoRefresh ? 'Otomatik yenileme AÇIK' : 'Otomatik yenileme KAPALI'}
              sx={{ mr: 1 }}
            >
              {autoRefresh ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateWorkOrder}
            >
              Yeni İş Emri
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Work Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>İş Emri No</TableCell>
              <TableCell>Başlık</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Öncelik</TableCell>
              <TableCell>Atanan Kişi</TableCell>
              <TableCell>Termin</TableCell>
              <TableCell>İlgili Alarm</TableCell>
              <TableCell>Ek Dosya</TableCell>
              <TableCell align="center">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredWorkOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                    {loading ? 'İş emirleri yükleniyor...' : 'İş emri bulunamadı'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredWorkOrders.map((workOrder) => (
                <TableRow key={workOrder.workOrderID}>
                  <TableCell>WO-{workOrder.workOrderID}</TableCell>
                  <TableCell>
                    <Tooltip title={workOrder.description || ''}>
                      <Typography>{workOrder.title}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={workOrder.workOrderStatus || 'N/A'}
                      color={getStatusColor(workOrder.workOrderStatus?.toLowerCase())}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={workOrder.priority || 'Orta'}
                      color={getPriorityColor(workOrder.priority?.toLowerCase())}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{getEmployeeName(workOrder.assignedUserID)}</TableCell>
                  <TableCell>{workOrder.dueDate ? new Date(workOrder.dueDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    {workOrder.relatedEntityID ? (
                      <Chip label={`Alarm #${workOrder.relatedEntityID}`} size="small" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {workOrder.externalReference && (
                      <Tooltip title="Eki Görüntüle">
                        <IconButton
                          size="small"
                          onClick={() => window.open(`https://app-rmmsapi.azurewebsites.net${workOrder.externalReference}`, '_blank')}
                          color="info"
                        >
                          <AttachFileIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {workOrder.workOrderStatus?.toLowerCase() === 'open' && (
                      <Tooltip title="İş Emrini Al">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(workOrder)}
                          color="success"
                        >
                          <TakeIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {workOrder.workOrderStatus?.toLowerCase() === 'inprogress' && (
                      <Tooltip title="İş Emrini Tamamla">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(workOrder)}
                          color="success"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="İş Emri Detayları">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(workOrder)}
                        color="primary"
                      >
                        <AssignmentIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="İş Emrini Sil">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteWorkOrder(workOrder.workOrderID)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Work Order Dialog */}
      <WorkOrderDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleSaveWorkOrder}
        workOrder={selectedWorkOrder}
        relatedAlarm={relatedAlarm}
        employees={employees}
        skills={skills}
        employeeSkills={employeeSkills}
        locations={locations}
        equipment={equipment}
        operations={operations}
        userId={userId}
        companyId={companyId}
      />

      {/* Completion Dialog */}
      <CompletionDialog
        open={openCompletionDialog}
        onClose={() => setOpenCompletionDialog(false)}
        workOrder={selectedWorkOrder}
        onComplete={handleCompleteWorkOrder}
      />

      {/* Work Order Details Dialog */}
      <WorkOrderDetailsDialog
        open={detailsDialogOpen}
        onClose={handleDetailsDialogClose}
        workOrder={selectedWorkOrderForDetails}
        onSuccess={handleDetailsDialogClose}
        userId={userId}
      />

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="İş Emrini Sil"
        content="Bu iş emrini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        type="yesNo"
      />
    </Box>
  );
};

// Work Order Dialog Component
const WorkOrderDialog = ({ open, onClose, onSave, workOrder, relatedAlarm, employees = [], skills = [], employeeSkills = [], locations = [], equipment = [], operations = [], userId, companyId }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    assignedTo: '',
    dueDate: new Date().toISOString().split('T')[0],
    location: '',
    equipment: '',
    operation: '',
    estimatedHours: '',
    requiredSkillId: '',
    attachments: []
  });

  useEffect(() => {
    if (workOrder) {
      setFormData({
        title: workOrder.title || '',
        description: workOrder.description || '',
        status: workOrder.workOrderStatus?.toLowerCase() || 'open',
        priority: workOrder.priority?.toLowerCase() || 'medium',
        assignedTo: workOrder.assignedUserID || '',
        dueDate: workOrder.dueDate ? new Date(workOrder.dueDate).toISOString().split('T')[0] : '',
        location: workOrder.location || '',
        equipment: workOrder.equipment || '',
        operation: workOrder.operation || '',
        estimatedHours: workOrder.estimatedHours || '',
        requiredSkillId: workOrder.requiredSkillId || '',
        attachments: workOrder.externalReference ? [{
          id: 'existing',
          name: 'Mevcut Ek',
          size: 0,
          type: 'existing',
          data: workOrder.externalReference,
          isExisting: true
        }] : []
      });
    } else if (relatedAlarm) {
      // Prefill fields based on alarm
      setFormData({
        title: `Alarm #${relatedAlarm.id} için İş Emri`,
        description: relatedAlarm.message || '',
        status: 'open',
        priority: (relatedAlarm.statusCode || 'medium').toLowerCase(),
        assignedTo: '',
        dueDate: new Date().toISOString().split('T')[0],
        location: '',
        equipment: '',
        estimatedHours: '',
        requiredSkillId: '',
        attachments: []
      });
    } else {
      setFormData({
        title: '',
        description: '',
        status: 'open',
        priority: 'medium',
        assignedTo: '',
        dueDate: new Date().toISOString().split('T')[0],
        location: '',
        equipment: '',
        estimatedHours: '',
        requiredSkillId: '',
        attachments: []
      });
    }
  }, [workOrder, relatedAlarm]);

  const handleFileUpload = (event) => {
    console.log('handleFileUpload called, files:', event.target.files);
    const files = Array.from(event.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    console.log('Filtered image files:', imageFiles);

    if (imageFiles.length !== files.length) {
      toast.error('Sadece resim dosyaları eklenebilir');
      return;
    }

    // Convert files to base64 for preview
    const newAttachments = [];
    Promise.all(
      imageFiles.map((file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              id: Date.now() + Math.random(),
              name: file.name,
              size: file.size,
              type: file.type,
              data: e.target.result,
              file: file
            });
          };
          reader.readAsDataURL(file);
        });
      })
    ).then((attachments) => {
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...attachments]
      }));
    });
  };

  const removeAttachment = (attachmentId) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(att => att.id !== attachmentId)
    }));
  };

  // Filter employees based on required skill
  const getFilteredEmployees = () => {
    if (!formData.requiredSkillId) {
      return employees; // No skill filter, show all employees
    }

    // Filter employees who have the required skill
    const employeesWithSkill = employeeSkills
      .filter(empSkill => empSkill.SkillId.toString() === formData.requiredSkillId.toString())
      .map(empSkill => empSkill.EmployeeId);

    return employees.filter(employee =>
      employeesWithSkill.includes(employee.Id)
    );
  };

  const handleSubmit = () => {
    if (!formData.title) {
      toast.error('Lütfen bir başlık girin');
      return;
    }

    // Prepare data for submission - use camelCase for ASP.NET Core default binding
    const submitData = {
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
      assignedUserId: formData.assignedTo || null,
      dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
      location: formData.location,
      equipment: formData.equipment,
      estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : null,
      createdByUserId: userId,
      companyId: companyId,
      base64Attachment: formData.attachments.length > 0 ? formData.attachments[0].data : null
    };

    onSave(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {workOrder ? 'İş Emrini Düzenle' : 'Yeni İş Emri Oluştur'}
      </DialogTitle>
      <DialogContent>
        {relatedAlarm && (
          <Box sx={{ mb: 2, p: 1, borderRadius: 1, bgcolor: 'info.light' }}>
            <Typography variant="body2">
              Alarm #{relatedAlarm.id} için oluşturuluyor — {relatedAlarm.tagName} ({relatedAlarm.tagId}) değer: {relatedAlarm.tagValue}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Başlık"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>İşlem/Görev</InputLabel>
              <Select
                value={formData.operation}
                label="İşlem/Görev"
                onChange={(e) => setFormData({ ...formData, operation: e.target.value })}
              >
                <MenuItem value="">Yok</MenuItem>
                {operations.map((operation) => (
                  <MenuItem key={operation.operationID} value={operation.name}>
                    {operation.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Açıklama"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Durum</InputLabel>
              <Select
                value={formData.status}
                label="Durum"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="open">Açık</MenuItem>
                <MenuItem value="inprogress">Devam Ediyor</MenuItem>
                <MenuItem value="completed">Tamamlandı</MenuItem>
                <MenuItem value="cancelled">İptal Edildi</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Öncelik</InputLabel>
              <Select
                value={formData.priority}
                label="Öncelik"
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <MenuItem value="low">Düşük</MenuItem>
                <MenuItem value="medium">Orta</MenuItem>
                <MenuItem value="high">Yüksek</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Atanan Kişi</InputLabel>
              <Select
                value={formData.assignedTo}
                label="Atanan Kişi"
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
              >
                <MenuItem value="">
                  <em>Atanmadı</em>
                </MenuItem>
                {getFilteredEmployees().map((employee) => (
                  <MenuItem key={employee.Id} value={employee.Id.toString()}>
                    {employee.FirstName} {employee.LastName} {employee.Title && `(${employee.Title})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Termin"
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Lokasyon</InputLabel>
              <Select
                value={formData.location}
                label="Lokasyon"
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              >
                <MenuItem value="">Yok</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.locationID} value={location.name}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Ekipman</InputLabel>
              <Select
                value={formData.equipment}
                label="Ekipman"
                onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
              >
                <MenuItem value="">Yok</MenuItem>
                {equipment.map((item) => (
                  <MenuItem key={item.equipmentID} value={item.name}>
                    {item.name} {item.model && `- ${item.model}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Tahmini Süre (saat)"
              type="number"
              inputProps={{ min: 0, step: 0.5 }}
              value={formData.estimatedHours}
              onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Gerekli Yetenek</InputLabel>
              <Select
                value={formData.requiredSkillId}
                label="Gerekli Yetenek"
                onChange={(e) => {
                  const newSkillId = e.target.value;
                  // Reset assigned user if skill changes (new skill may not be compatible)
                  setFormData({
                    ...formData,
                    requiredSkillId: newSkillId,
                    assignedTo: '' // Reset assignment when skill changes
                  });
                }}
              >
                <MenuItem value="">
                  <em>Yetenek Gerektirmiyor</em>
                </MenuItem>
                {skills.map((skill) => (
                  <MenuItem key={skill.Id} value={skill.Id.toString()}>
                    {skill.Name} {skill.Category && `(${skill.Category})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              <AttachFileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Ekler
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              sx={{ mb: 2 }}
            >
              Fotoğraf Yükle
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleFileUpload}
              />
            </Button>
            {formData.attachments.length > 0 && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {formData.attachments.map((attachment) => (
                  <Box
                    key={attachment.id}
                    sx={{
                      position: 'relative',
                      width: 100,
                      height: 100,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={attachment.data}
                      alt={attachment.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bgcolor: 'rgba(255,255,255,0.7)'
                      }}
                      onClick={() => removeAttachment(attachment.id)}
                      color="error"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>İptal</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {workOrder ? 'Güncelle' : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Completion Dialog Component
const CompletionDialog = ({ open, onClose, workOrder, onComplete }) => {
  const [formData, setFormData] = useState({
    completionNotes: '',
    actualHours: '',
    finalStatus: 'Completed',
    attachments: []
  });

  useEffect(() => {
    if (workOrder && workOrder.externalReference) {
      setFormData(prev => ({
        ...prev,
        attachments: [{
          id: 'existing',
          name: 'Mevcut Ek',
          size: 0,
          type: 'existing',
          data: workOrder.externalReference,
          isExisting: true
        }]
      }));
    }
  }, [workOrder]);

  const handleSubmit = () => {
    if (!formData.completionNotes.trim()) {
      toast.error('Lütfen tamamlama notları girin');
      return;
    }

    onComplete({
      completionNotes: formData.completionNotes,
      actualHours: formData.actualHours ? parseFloat(formData.actualHours) : null,
      finalStatus: formData.finalStatus,
      base64Attachment: formData.attachments.length > 0 ? formData.attachments[0].data : null
    });
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);

    // Allow any file type for completion attachments
    const newAttachments = [];
    Promise.all(
      files.map((file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              id: Date.now() + Math.random(),
              name: file.name,
              size: file.size,
              type: file.type,
              data: e.target.result,
              file: file
            });
          };
          reader.readAsDataURL(file);
        });
      })
    ).then((attachments) => {
      setFormData({ ...formData, attachments: [...formData.attachments, ...attachments] });
    });

    event.target.value = '';
  };

  const removeAttachment = (id) => {
    setFormData({
      ...formData,
      attachments: formData.attachments.filter(attachment => attachment.id !== id)
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>İş Emrini Tamamla - WO-{workOrder?.workOrderID}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>{workOrder?.title}</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Tahmini Süre: {workOrder?.estimatedHours || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tamamlama Notları *"
              multiline
              rows={4}
              value={formData.completionNotes}
              onChange={(e) => setFormData({ ...formData, completionNotes: e.target.value })}
              placeholder="Tamamlanan işleri ve önemli notları açıklayın..."
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Gerçekleşen Süre (saat)"
              type="number"
              value={formData.actualHours}
              onChange={(e) => setFormData({ ...formData, actualHours: e.target.value })}
              inputProps={{ min: 0, step: 0.5 }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Son Durum</InputLabel>
              <Select
                value={formData.finalStatus}
                onChange={(e) => setFormData({ ...formData, finalStatus: e.target.value })}
                label="Son Durum"
              >
                <MenuItem value="Completed">Tamamlandı</MenuItem>
                <MenuItem value="Cancelled">İptal Edildi</MenuItem>
                <MenuItem value="On Hold">Beklemede</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <input
              accept="*/*"
              style={{ display: 'none' }}
              id="completion-file-upload"
              multiple
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="completion-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<AttachFileIcon />}
                sx={{ mt: 1 }}
              >
                Tamamlama Fotoğrafı/Belgesi Ekle
              </Button>
            </label>
            {formData.attachments.length > 0 && (
              <List dense sx={{ mt: 2 }}>
                {formData.attachments.map((attachment) => (
                  <ListItem key={attachment.id}>
                    <Avatar sx={{ mr: 2 }}>
                      {attachment.type.startsWith('image/') ? <ImageIcon /> : <DescriptionIcon />}
                    </Avatar>
                    <ListItemText
                      primary={attachment.name}
                      secondary={`${(attachment.size / 1024).toFixed(1)} KB`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => removeAttachment(attachment.id)}
                        color="error"
                      >
                        <CloseIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>İptal</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!formData.completionNotes.trim()}
        >
          İş Emrini Tamamla
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkOrders;
